// Données de TEST (aperçu) — construites à partir du scénario de conversation « Léa & Thomas ».
// Aucune donnée réelle, aucun paiement. Sert à prévisualiser l'écran d'analyse RawLove.
export const ANALYSIS_FIXTURE = {
  first_name: "Léa",
  partner_name: "Thomas",
  generated_at: "2026-06-12",
  central_question: "Est-ce qu'il s'éloigne vraiment de moi ou c'est juste une passe liée à son travail ?",
  intro:
    "Tu m'as raconté quatre mois avec Thomas, et deux semaines qui ne ressemblent plus au début. Voici ce que j'ai retenu de ce que tu as réellement observé, ce que tu en penses, et ce qu'on peut — ou pas — en conclure aujourd'hui.",
  facts: [
    "Depuis environ deux semaines, ses réponses arrivent en plusieurs heures, alors qu'avant c'était dans la minute.",
    "Il a annulé votre week-end à Annecy trois jours avant, en disant être « épuisé par le boulot ».",
    "Vous vous êtes vus une fois en deux semaines, contre deux à trois fois par semaine auparavant.",
    "Lors de cette rencontre, il était tendre, puis il est parti vers 22h pour « se lever tôt ».",
    "Depuis deux semaines, c'est toi qui proposes de vous voir ; avant, c'était plutôt lui.",
    "Il parle d'un gros dossier client en cours depuis un mois.",
    "Il n'a exprimé aucun reproche et n'a jamais parlé de faire une pause.",
    "Tu ne lui as pas encore dit ce que tu ressens.",
  ],
  facts_vs_interpretations: [
    {
      fact: "Il répond en plusieurs heures depuis deux semaines.",
      interpretation: "Il perd de l'intérêt pour moi.",
      hypothesis: "Sa disponibilité a diminué ; la cause (travail, relation, autre) n'est pas établie.",
    },
    {
      fact: "C'est toi qui proposes de vous voir depuis deux semaines.",
      interpretation: "Je l'étouffe et il n'ose pas me le dire.",
      hypothesis: "L'initiative s'est déplacée vers toi ; rien dans ses mots n'indique qu'il se sente étouffé.",
    },
    {
      fact: "Il a annulé le week-end en invoquant l'épuisement.",
      interpretation: "Il a rencontré quelqu'un d'autre.",
      hypothesis: "Aucun élément de la conversation ne soutient cette lecture ; c'est une peur, pas une observation.",
    },
  ],
  hypotheses: [
    {
      title: "Une période professionnelle qui déborde",
      supporting: [
        "Il évoque ce dossier client depuis un mois, avant même le changement.",
        "Il reste tendre en face à face et n'a formulé aucun reproche.",
      ],
      limits: [
        "Le travail explique la fatigue, pas forcément la baisse d'initiative.",
        "Il n'a pas proposé de date pour rattraper le week-end.",
      ],
    },
    {
      title: "Un recul sur la relation qu'il n'a pas encore nommé",
      supporting: [
        "Baisse simultanée des messages, des rencontres et de l'initiative.",
        "Un « on fera ça bientôt » sans date concrète.",
      ],
      limits: [
        "Il n'a exprimé aucun doute ni parlé de pause.",
        "Deux semaines, c'est court pour distinguer une passe d'une tendance.",
      ],
    },
    {
      title: "Un décalage de rythme qui n'a jamais été discuté",
      supporting: [
        "Tu gardes ton inquiétude pour toi ; il ne sait pas ce que ça te fait.",
        "Le changement coïncide avec une charge de travail qu'il te décrit lui-même.",
      ],
      limits: [
        "On ne connaît pas sa perception de la situation, puisqu'elle n'a pas été posée.",
      ],
    },
  ],
  red_flags: [
    { title: "Aucune date proposée pour rattraper Annecy", why: "Annuler arrive ; ne rien reproposer dit quelque chose de l'attention portée à toi, à surveiller sur la durée." },
    { title: "L'initiative repose désormais entièrement sur toi", why: "Un déséquilibre installé sur plusieurs semaines pèse sur la réciprocité et sur ton sentiment de sécurité." },
  ],
  green_flags: [
    { title: "Tendresse et présence quand vous êtes ensemble", why: "Le lien en face à face n'a pas changé de qualité : c'est la fréquence qui a baissé, pas la chaleur." },
    { title: "Il te donne une explication, même partielle", why: "Il a nommé son épuisement plutôt que de disparaître en silence." },
    { title: "Aucun reproche, aucun signe d'agacement", why: "Rien dans ses mots ne va dans le sens de l'idée que tu serais « trop »." },
  ],
  answer: {
    summary:
      "Tu n'inventes pas le changement : plusieurs comportements ont effectivement évolué en deux semaines. En revanche, les informations disponibles ne permettent pas encore d'en connaître la cause avec certitude.",
    detail: [
      "Ce qui est sûr : moins de disponibilité, moins de rencontres, une initiative qui a changé de camp et un week-end annulé sans rattrapage. Ce sont des faits, pas une impression.",
      "Ce qui ne l'est pas : ce que ces changements signifient pour Thomas. Le contexte professionnel qu'il décrit est réel et antérieur au changement, ce qui rend l'hypothèse « passe liée au travail » crédible — sans la prouver.",
      "La seule information qui départagerait vraiment ces hypothèses, c'est sa réponse à toi. Elle n'existe pas encore, parce que la question ne lui a pas été posée.",
    ],
  },
  action_plan: [
    {
      title: "Dire ce que tu vis, sans dresser la liste de ce qu'il fait",
      detail: "Parler de toi plutôt que de lui évite le procès et ouvre la porte à sa version.",
      script: "« J'ai remarqué qu'on se voyait moins ces deux dernières semaines et je me sens un peu perdue. Je préfère t'en parler que de me faire des films. Toi, tu vis ça comment en ce moment ? »",
    },
    {
      title: "Proposer une date précise pour Annecy",
      detail: "Sa réaction à une proposition concrète t'en dira plus que des semaines d'attente : accepte-t-il, propose-t-il autre chose, ou reste-t-il vague ?",
    },
    {
      title: "Observer deux semaines de plus, sans relancer plus que d'habitude",
      detail: "Garde ton rythme habituel de messages. Si l'initiative revient de son côté quand son dossier se termine, l'hypothèse professionnelle se confirme ; sinon, tu auras une information nouvelle.",
    },
  ],
  known: [
    "Le rythme de la relation a changé il y a deux semaines, sur plusieurs plans à la fois.",
    "Il traverse une période de travail intense qu'il te décrit lui-même.",
    "En face à face, il reste tendre et n'a exprimé aucun reproche.",
    "Tu portes seule ton inquiétude depuis le début du changement.",
  ],
  unknown: [
    "Ce que Thomas ressent réellement pour la relation aujourd'hui.",
    "S'il a conscience du changement que tu observes.",
    "Si l'initiative reviendra de son côté une fois son dossier terminé.",
    "Comment il réagirait si tu lui disais ce que tu vis.",
  ],
  closing:
    "Tu as fait le plus difficile : regarder la situation telle qu'elle est, sans la noircir ni la maquiller. Le reste ne se joue plus dans ta tête, mais dans une conversation à deux — quand tu te sentiras prête.",
  safety: "",
};
